/**
 * The open-weight models OculusMind.AI publishes, and the figures `/models`
 * shows for them.
 *
 * The published model card is the source of truth: the README of
 * huggingface.co/OculusMindAI/OculusMind-ToolCall-8B-v2 (published 2026-10-07 as
 * OculusMindAI/Pico-V2, hub commit 13e62b76; renamed 2026-10-08, the old id
 * redirects). Every number here is COPIED from that card's tables — nothing is
 * measured or recomputed in this repo, except the deltas, which the page derives
 * from the two numbers it shows so the three can never disagree.
 */

export interface PublishedModel {
  readonly name: string;
  /** Hugging Face repo id, e.g. `OculusMindAI/OculusMind-ToolCall-8B-v2`. */
  readonly repoId: string;
  readonly url: string;
}

export const PICO_V2: PublishedModel = {
  name: 'Pico V2',
  repoId: 'OculusMindAI/OculusMind-ToolCall-8B-v2',
  url: 'https://huggingface.co/OculusMindAI/OculusMind-ToolCall-8B-v2',
};

/**
 * Pico V2 in the Ollama catalog (published 2026-10-10 from the models repo's
 * `scripts/studio/push-ollama-com.sh`; owner decision pico-v2-ollama-registry).
 * `command` is exactly what the model card and its Ollama walkthrough tell people
 * to type, so the three cannot disagree. Ollama names are not case-sensitive: the
 * catalog page prints `OculusMindAI/…`, and the lowercase name was verified by a
 * pull from the catalog with no local copy (models repo, verify section O).
 */
export interface OllamaListing {
  readonly name: string;
  readonly url: string;
  readonly command: string;
}

export const PICO_V2_OLLAMA: OllamaListing = {
  name: 'oculusmindai/toolcall-8b-v2',
  // The page's canonical address: the lowercase one answers 303 to this.
  url: 'https://ollama.com/OculusMindAI/toolcall-8b-v2',
  command: 'ollama run oculusmindai/toolcall-8b-v2',
};

/** The first generation of the series. */
export const PICO_V1: PublishedModel = {
  name: 'OculusMind-ToolCall-8B-v1',
  repoId: 'OculusMindAI/OculusMind-ToolCall-8B-v1',
  url: 'https://huggingface.co/OculusMindAI/OculusMind-ToolCall-8B-v1',
};

/**
 * One row of the card's headline table: an OculusMind.AI production-derived
 * eval population, scored for the untrained base and Pico V2.
 * `metric: 'agreement'` is the % of tool-turns where the model chose the same
 * first tool production chose, with valid arguments (higher is better);
 * `'loops'` is the % of turns with a degenerate repeated tool call (lower is
 * better).
 */
export interface PopulationRow {
  readonly population: string;
  readonly detail: string;
  readonly metric: 'agreement' | 'loops';
  readonly base: number;
  readonly pico: number;
}

export const POPULATION_ROWS: readonly PopulationRow[] = [
  {
    population: 'report-vera-sealed',
    detail: 'held out from every selection decision, 66 tool-turns — the reported figure',
    metric: 'agreement',
    base: 86.36,
    pico: 95.45,
  },
  {
    population: 'select-vera',
    detail: '146 tool-turns, temperature 0',
    metric: 'agreement',
    base: 80.82,
    pico: 96.58,
  },
  {
    population: 'select-vera',
    detail: 'at production temperature 0.35',
    metric: 'agreement',
    base: 82.88,
    pico: 97.26,
  },
  {
    population: 'loop-provocation slice',
    detail: '12 turns built to provoke tool-call loops',
    metric: 'agreement',
    base: 83.33,
    pico: 100,
  },
  {
    population: 'degenerate loops',
    detail: 'on the loop-provocation slice — lower is better',
    metric: 'loops',
    base: 8.33,
    pico: 0,
  },
];

/** One BFCL V4 category: item count and accuracy (%) for the base and Pico V2. */
export interface BfclRow {
  readonly category: string;
  readonly n: number;
  readonly base: number;
  readonly pico: number;
}

export const BFCL_ROWS: readonly BfclRow[] = [
  { category: 'simple_python', n: 400, base: 88.5, pico: 92.25 },
  { category: 'simple_java', n: 100, base: 20, pico: 44 },
  { category: 'simple_javascript', n: 50, base: 32, pico: 48 },
  { category: 'multiple', n: 200, base: 88.5, pico: 93 },
  { category: 'parallel', n: 200, base: 83.5, pico: 87.5 },
  { category: 'parallel_multiple', n: 200, base: 76, pico: 81.5 },
  { category: 'irrelevance', n: 240, base: 89.58, pico: 86.25 },
  { category: 'live_simple', n: 258, base: 59.69, pico: 65.5 },
  { category: 'live_multiple', n: 1053, base: 68.19, pico: 69.99 },
  { category: 'live_parallel', n: 16, base: 50, pico: 68.75 },
  { category: 'live_parallel_multiple', n: 24, base: 62.5, pico: 75 },
  { category: 'live_irrelevance', n: 884, base: 85.29, pico: 83.03 },
  { category: 'live_relevance', n: 16, base: 81.25, pico: 87.5 },
  { category: 'multi_turn_base', n: 200, base: 23.5, pico: 37.5 },
  { category: 'multi_turn_miss_func', n: 200, base: 9.5, pico: 14 },
  { category: 'multi_turn_miss_param', n: 200, base: 14, pico: 26.5 },
  { category: 'multi_turn_long_context', n: 200, base: 18.5, pico: 35 },
];

/**
 * Item-weighted accuracy over every category, the card's "BFCL V4 overall".
 * Each category's accuracy is an exact fraction of its items, so the correct
 * count is recovered by rounding and summed rather than averaging percentages.
 */
export function bfclOverall(rows: readonly BfclRow[], model: 'base' | 'pico'): number {
  let items = 0;
  let correct = 0;
  for (const row of rows) {
    items += row.n;
    correct += Math.round((row[model] * row.n) / 100);
  }
  return (correct / items) * 100;
}
